<template>
  <div class="teacher-page d-flex">
    <aside class="side-nav" :class="{ collapsed: sidebarCollapsed }">
      <div class="side-head">
        <h4 class="mb-0" v-show="!sidebarCollapsed">教师端</h4>
        <button class="collapse-btn" @click="toggleSidebar">{{ sidebarCollapsed ? '>>' : '<<' }}</button>
      </div>
      <button class="sub-btn" @click="goQuestions"><span class="nav-text">题库管理</span></button>
      <button class="sub-btn" @click="goPapers"><span class="nav-text">试卷管理</span></button>
      <button class="sub-btn" @click="goExamManage"><span class="nav-text">考试管理</span></button>
      <button class="sub-btn active"><span class="nav-text">阅卷工作台</span></button>
      <button class="sub-btn" @click="goExamRecordManage"><span class="nav-text">考生试卷管理</span></button>
      <button class="sub-btn" @click="goProctor"><span class="nav-text">监考系统</span></button>
      <button class="sub-btn" @click="goStudentUsers"><span class="nav-text">学生用户管理</span></button>
      <div class="mt-auto">
        <button class="btn btn-sm btn-light w-100 mb-2" @click="backToGradingList">返回阅卷列表</button>
      </div>
    </aside>

    <main class="content-area">
      <header class="topbar">
        <h2>教师阅卷工作台</h2>
        <div class="actions">
          <button class="btn btn-light btn-sm" @click="loadDetail">刷新</button>
          <button class="btn btn-light btn-sm" @click="goPrevQuestion">上一题</button>
          <button class="btn btn-primary btn-sm" @click="goNextPendingQuestion">下一题(未评分)</button>
          <button class="btn btn-success btn-sm" @click="submitRecordGrade">提交本学员阅卷</button>
        </div>
      </header>

      <section class="card-box mb-3">
        <div class="meta-grid">
          <div><span class="k">记录ID</span><span class="v">{{ recordMeta.recordId || '-' }}</span></div>
          <div><span class="k">学员ID</span><span class="v">{{ recordMeta.userId || '-' }}</span></div>
          <div><span class="k">账号</span><span class="v">{{ recordMeta.username || '-' }}</span></div>
          <div><span class="k">状态</span><span class="v">{{ recordMeta.status || '-' }}</span></div>
          <div><span class="k">当前总分</span><span class="v">{{ recordMeta.score || 0 }}</span></div>
          <div><span class="k">交卷时间</span><span class="v">{{ $fmtDateTime(recordMeta.submitTime) }}</span></div>
        </div>
      </section>

      <section class="card-box workspace">
        <div class="left-pane">
          <h5 class="mb-2">待阅卷题目（编程题 / 资料收集 / Scratch 等）</h5>
          <div class="row g-2 mb-2">
            <div class="col-md-6">
              <select v-model="codingFilterStatus" class="form-select form-select-sm">
                <option value="all">显示全部</option>
                <option value="pending">仅看未评分</option>
                <option value="graded">仅看已评分</option>
              </select>
            </div>
            <div class="col-md-6">
              <input v-model="codingKeyword" class="form-control form-control-sm" placeholder="按题干关键字筛选" />
            </div>
          </div>
          <div class="mb-2">
            <select
              v-if="filteredWorkspaceRows.length > 0"
              v-model="currentQuestionId"
              class="form-select form-select-sm"
              @change="pickQuestionById(currentQuestionId)"
            >
              <option v-for="q in filteredWorkspaceRows" :key="`opt-${q.questionId}`" :value="q.questionId">
                {{ q.sort }}. {{ q.title || '未命名题目' }}（{{ isAnswerManuallyGraded(q) ? '已评分' : '未评分' }}）
              </option>
            </select>
          </div>
          <div class="question-list">
            <button
              v-for="(q, idx) in filteredWorkspaceRows"
              :key="q.questionId"
              class="q-btn"
              :class="{ active: currentQuestion && currentQuestion.questionId === q.questionId }"
              @click="pickQuestionById(q.questionId)"
            >
              {{ idx + 1 }}. {{ q.typeText || q.type }} / {{ q.title || '未命名题目' }}（{{ isAnswerManuallyGraded(q) ? '已评分' : '未评分' }}）
            </button>
            <div v-if="filteredWorkspaceRows.length === 0" class="text-muted small">
              <template v-if="workspaceRows.length === 0">本场答卷没有编程题、资料收集或 Scratch 题。</template>
              <template v-else-if="codingFilterStatus === 'pending'">
                当前「仅看未评分」下没有题目。自动判题完成后的编程题会进入「已评分」，请切换为「显示全部」或「仅看已评分」。
              </template>
              <template v-else-if="codingFilterStatus === 'graded'">当前「仅看已评分」下没有题目，可切换为「显示全部」。</template>
              <template v-else>暂无匹配的待阅卷题目（可清空关键字筛选）。</template>
            </div>
          </div>

          <div class="question-detail mt-3" v-if="currentQuestion">
            <h6>{{ currentQuestion.typeText || currentQuestion.type }}（题号 {{ currentQuestion.sort }}）</h6>
            <p class="mb-2 gw-stem"><strong>题干：</strong></p>
            <pre class="gw-pre gw-stem-body mb-2">{{ currentQuestion.title || '-' }}</pre>
            <div v-if="gradingStemImages(currentQuestion).length" class="d-flex flex-wrap gap-2 mb-2">
              <img
                v-for="(u, gi) in gradingStemImages(currentQuestion)"
                :key="'gw-st-' + gi"
                :src="u"
                class="gw-q-thumb"
                alt="题干图"
                loading="lazy"
                @click="openGradingImg(u)"
              />
            </div>
            <div v-if="gradingOptionImages(currentQuestion).length" class="small text-muted mb-1">选项附图</div>
            <div v-if="gradingOptionImages(currentQuestion).length" class="d-flex flex-wrap gap-2 mb-2">
              <img
                v-for="(im, xi) in gradingOptionImages(currentQuestion)"
                :key="'gw-op-' + xi"
                :src="im.url"
                class="gw-q-thumb"
                :title="im.label"
                loading="lazy"
                alt=""
                @click="openGradingImg(im.url)"
              />
            </div>
            <div v-if="isMaterialCollectType(currentQuestion.type)" class="mb-2 gw-material-block">
              <strong>学生作答（资料收集）</strong>
              <div class="small text-muted mt-2 mb-1">文字说明</div>
              <pre class="gw-pre mb-2">{{ currentQuestion.materialCollectText || materialCollectDraftText(currentQuestion) || '—' }}</pre>
              <div class="small text-muted mb-1">附件（可预览 / 下载后阅卷，并在下方「手动评分」打分）</div>
              <ul v-if="materialCollectAttList(currentQuestion).length" class="list-group list-group-flush border rounded mb-0">
                <li
                  v-for="(a, ai) in materialCollectAttList(currentQuestion)"
                  :key="'gw-mc-' + ai"
                  class="list-group-item d-flex flex-wrap justify-content-between align-items-start gap-2 py-2"
                >
                  <div class="d-flex flex-wrap align-items-start gap-2 flex-grow-1 min-w-0">
                    <button
                      v-if="isMaterialImageAttachment(a)"
                      type="button"
                      class="gw-mat-thumb-wrap btn p-0 border-0 bg-transparent"
                      @click="openMaterialImagePreview(a)"
                      title="点击放大预览"
                    >
                      <img v-if="materialImageDisplaySrc(a)" :src="materialImageDisplaySrc(a)" class="gw-mat-thumb" alt="" loading="lazy" />
                      <span v-else class="gw-mat-thumb gw-mat-thumb-ph small text-muted">加载中</span>
                    </button>
                    <div class="small min-w-0">
                      <div class="fw-medium text-break">{{ a.originalName || a.storageName || '附件' }}</div>
                      <div v-if="a.size != null" class="text-muted">{{ formatMaterialFileSize(a.size) }}</div>
                    </div>
                  </div>
                  <span class="flex-shrink-0 d-flex flex-wrap gap-1">
                    <button
                      v-if="a.viewUrl"
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      @click="openMaterialWithAuth(a, 'view')"
                    >浏览器中打开</button>
                    <button
                      v-if="a.downloadUrl"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click="openMaterialWithAuth(a, 'download')"
                    >下载</button>
                  </span>
                </li>
              </ul>
              <div v-else class="text-muted small">无附件</div>
            </div>
            <div v-else-if="isScratchType(currentQuestion.type)" class="mb-2">
              <strong>学生作答（Scratch）</strong>
              <div v-if="scratchArtifact(currentQuestion).originalName" class="small mt-2 mb-1">
                项目文件：{{ scratchArtifact(currentQuestion).originalName }}
                <span v-if="scratchArtifact(currentQuestion).size" class="text-muted">
                  （{{ formatMaterialFileSize(scratchArtifact(currentQuestion).size) }}）</span>
              </div>
              <div class="d-flex flex-wrap gap-2 mb-2 align-items-center">
                <button
                  v-if="scratchArtifact(currentQuestion).downloadUrl"
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="openScratchDownload(currentQuestion)"
                >下载 .sb3</button>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="openScratchGradingFullPage(currentQuestion)"
                >全屏打开 Scratch 阅卷</button>
              </div>
              <p v-if="!scratchArtifact(currentQuestion).downloadUrl" class="text-muted small mb-2">该生尚未上传项目，无法在线打开。</p>
              <label class="form-label small">评语（与评分一并保存）</label>
              <textarea
                v-model="scratchCommentDraft[currentQuestion.questionId]"
                class="form-control mb-0"
                rows="2"
                placeholder="可选：对该生 Scratch 作品的评语"
              />
            </div>
            <div v-else-if="isCodingType(currentQuestion.type)" class="mb-2">
              <div class="small fw-bold mb-1">学生作答（第 {{ currentQuestion.sort || '-' }} 题 · ID {{ currentQuestion.questionId }}）</div>
              <textarea
                class="form-control code-editor student-code-readonly"
                readonly
                rows="8"
                :value="currentQuestion.userAnswer || ''"
              ></textarea>
              <div v-if="currentCodingJudgeDetail" class="mt-2 small">
                <div class="fw-semibold mb-1">自动评分</div>
                <p class="mb-1">
                  {{ currentCodingJudgeDetail.passedCases || 0 }}/{{ currentCodingJudgeDetail.totalCases || 0 }} 个用例通过，
                  得分 {{ currentCodingJudgeDetail.score ?? currentQuestion.answerScore ?? 0 }}/{{ currentCodingJudgeDetail.maxScore ?? currentQuestion.maxScore ?? 0 }}
                  <span class="text-muted">（输出正确即满分）</span>
                </p>
                <p v-if="currentCodingBonusHits.length" class="mb-1 text-success small">
                  写法参考：{{ currentCodingBonusHits.map(bonusHintLabel).join('、') }}
                </p>
                <table v-if="currentCodingJudgeCaseResults.length" class="table table-sm table-bordered mb-0">
                  <thead>
                    <tr><th>用例</th><th>结果</th><th>说明</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(cr, i) in currentCodingJudgeCaseResults" :key="'jd-' + i">
                      <td>{{ cr.sample ? '样例' : '判分' }}{{ cr.caseId ? ' ' + cr.caseId : (i + 1) }}</td>
                      <td :class="cr.status === 'AC' ? 'text-success' : 'text-danger'">{{ cr.status === 'AC' ? '通过' : (cr.status || '未通过') }}</td>
                      <td>{{ cr.message || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p v-else class="mb-2"><strong>学生作答：</strong>{{ currentQuestion.userAnswer || '-' }}</p>
            <p class="mb-2"><strong>参考答案：</strong>{{ currentQuestion.correctAnswer || '-' }}</p>
            <p class="mb-2"><strong>满分：</strong>{{ currentQuestion.maxScore ?? 0 }}</p>
            <p v-if="currentQuestion.analysis" class="mb-2"><strong>解析：</strong>{{ currentQuestion.analysis }}</p>
            <div v-if="gradingAnalysisImages(currentQuestion).length" class="small text-muted mb-1">解析附图</div>
            <div v-if="gradingAnalysisImages(currentQuestion).length" class="d-flex flex-wrap gap-2 mb-2">
              <img
                v-for="(u, ai) in gradingAnalysisImages(currentQuestion)"
                :key="'gw-an-' + ai"
                :src="u"
                class="gw-q-thumb"
                alt="解析图"
                loading="lazy"
                @click="openGradingImg(u)"
              />
            </div>
            <div class="row g-2 align-items-end" v-if="isManualType(currentQuestion.type)">
              <div class="col-md-4">
                <label class="form-label">手动评分</label>
                <input v-model.number="manualScoreDraft[currentQuestion.questionId]" type="number" min="0" :max="currentQuestion.maxScore || 0" class="form-control" />
              </div>
              <div class="col-md-4">
                <button class="btn btn-primary w-100" @click="saveSingleScore(currentQuestion)">保存该题评分</button>
              </div>
              <div class="col-md-4">
                <button class="btn btn-light w-100" @click="saveSingleScoreAndNext(currentQuestion)">保存后下一题</button>
              </div>
            </div>
          </div>
        </div>

        <div class="right-pane">
          <template v-if="currentQuestion && isCodingType(currentQuestion.type)">
            <h5 class="mb-2">Python 运行区</h5>
            <textarea v-model="runCode" class="form-control code-editor" placeholder="输入或粘贴 Python 代码"></textarea>
            <div class="d-flex gap-2 mt-2">
              <button class="btn btn-success" :disabled="runningCode" @click="runPythonCode">运行代码</button>
              <button class="btn btn-light" @click="fillCodeFromAnswer">使用学生代码</button>
            </div>
            <div class="result-shell mt-3">
              <div class="small mb-1"><strong>运行输出(stdout)</strong></div>
              <pre class="run-box">{{ runStdout || '-' }}</pre>
              <div class="small mb-1 mt-2"><strong>运行错误(stderr)</strong></div>
              <pre class="run-box">{{ runStderr || '-' }}</pre>
              <div class="small mt-2"><strong>判题结果：</strong>{{ runJudgeText }}</div>
              <table v-if="runCaseResults.length" class="table table-sm table-bordered mt-2 mb-0 small">
                <thead>
                  <tr><th>用例</th><th>状态</th><th>说明</th><th>期望</th><th>实际</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(cr, i) in runCaseResults" :key="'run-cr-' + i">
                    <td>{{ cr.caseId || ('用例' + (i + 1)) }}</td>
                    <td>{{ cr.status || '-' }}</td>
                    <td>{{ cr.message || '-' }}</td>
                    <td class="font-monospace text-break">{{ truncateText(cr.expected) }}</td>
                    <td class="font-monospace text-break">{{ truncateText(cr.actual) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          <template v-else-if="currentQuestion && isScratchType(currentQuestion.type)">
            <h5 class="mb-2">Scratch 阅卷</h5>
            <p class="small text-muted mb-3">
              使用与学员相同的 Scratch 编辑器打开该生 .sb3，可运行项目、查看积木与舞台。
            </p>
            <button type="button" class="btn btn-primary" @click="openScratchGradingFullPage(currentQuestion)">全屏打开 Scratch 阅卷</button>
          </template>
          <template v-else-if="currentQuestion && isMaterialCollectType(currentQuestion.type)">
            <h5 class="mb-2">资料收集题 · 附件阅卷</h5>
            <p class="small text-muted mb-3">
              请下载或在浏览器中打开附件核对内容，结合左侧文字说明在下方「手动评分」输入分数并保存。图片可点击缩略图放大预览。
            </p>
            <div v-if="materialCollectAttList(currentQuestion).length" class="gw-mat-side-list">
              <div
                v-for="(a, ai) in materialCollectAttList(currentQuestion)"
                :key="'gw-mc-side-' + ai"
                class="border rounded p-2 mb-2 bg-white"
              >
                <div class="small fw-semibold text-break mb-2">{{ a.originalName || a.storageName || '附件' }}</div>
                <div class="d-flex flex-wrap gap-1 mb-2">
                  <button v-if="a.viewUrl" type="button" class="btn btn-sm btn-outline-primary" @click="openMaterialWithAuth(a, 'view')">打开</button>
                  <button v-if="a.downloadUrl" type="button" class="btn btn-sm btn-outline-secondary" @click="openMaterialWithAuth(a, 'download')">下载</button>
                </div>
                <button
                  v-if="isMaterialImageAttachment(a)"
                  type="button"
                  class="btn p-0 border gw-mat-side-thumb"
                  @click="openMaterialImagePreview(a)"
                >
                  <img v-if="materialImageDisplaySrc(a)" :src="materialImageDisplaySrc(a)" alt="" loading="lazy" />
                  <span v-else class="small text-muted p-2 d-block">图片加载中…</span>
                </button>
              </div>
            </div>
            <div v-else class="text-muted small">本题无附件记录</div>
          </template>
          <template v-else>
            <h5 class="mb-2">辅助区</h5>
            <p class="text-muted small">请从左侧选择一道编程题、资料收集题或 Scratch 题。</p>
          </template>
        </div>
      </section>
    </main>

    <div v-if="gradingImgPreview" class="gw-img-mask" @click.self="closeGradingImg">
      <div class="gw-img-panel" @click.stop @wheel.prevent="onGradingImgWheel">
        <div class="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
          <span class="small text-muted">滚轮或按钮缩放</span>
          <div class="d-flex flex-wrap gap-1">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="gradingImgScale = Math.min(4, gradingImgScale + 0.2)">放大</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="gradingImgScale = Math.max(0.25, gradingImgScale - 0.2)">缩小</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="gradingImgScale = 1">复位</button>
            <button type="button" class="btn btn-sm btn-light" @click="closeGradingImg">关闭</button>
          </div>
        </div>
        <div class="gw-img-viewport">
          <img :src="gradingImgPreview" alt="预览" class="gw-img-zoomed" :style="{ transform: 'scale(' + gradingImgScale + ')' }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseUrlList, optionEntriesFromRow as buildQuestionOptionEntries } from '../utils/questionRichMedia'
import { MATERIAL_COLLECT_TYPE, parseMaterialAnswer, extOfFilename } from '../utils/materialCollect'
import { SCRATCH_TYPE, parseScratchAnswer } from '../utils/scratchExam'
import { parseJudgeDetail, bonusHintLabel } from '../utils/codingJudge'

export default {
  name: 'TeacherGradingWorkspaceView',
  data() {
    return {
      recordMeta: {
        recordId: null,
        userId: null,
        username: '',
        status: '',
        score: 0,
        submitTime: ''
      },
      rows: [],
      currentQuestionId: null,
      manualScoreDraft: {},
      runCode: '',
      runStdout: '',
      runStderr: '',
      runJudgeText: '-',
      runCaseResults: [],
      runningCode: false,
      sidebarCollapsed: false,
      codingFilterStatus: 'all',
      codingKeyword: '',
      gradingImgPreview: null,
      gradingImgScale: 1,
      /** 教师打开附件需带 X-Role，不能用裸链；此处缓存 fetch 得到的 blob URL */
      materialBlobUrlByKey: {},
      scratchCommentDraft: {}
    }
  },
  computed: {
    pendingManualRows() {
      return this.rows.filter((x) => x && this.isManualType(x.type) && !this.isAnswerManuallyGraded(x))
    },
    /** 编程题 + 资料收集题等需人工阅卷的题目 */
    workspaceRows() {
      return this.rows.filter((x) => x && this.isManualType(x.type))
    },
    filteredWorkspaceRows() {
      let rows = this.workspaceRows
      if (this.codingFilterStatus === 'pending') {
        rows = rows.filter((x) => !this.isAnswerManuallyGraded(x))
      } else if (this.codingFilterStatus === 'graded') {
        rows = rows.filter((x) => this.isAnswerManuallyGraded(x))
      }
      if (this.codingKeyword && this.codingKeyword.trim()) {
        const kw = this.codingKeyword.trim().toLowerCase()
        rows = rows.filter((x) => String(x.title || '').toLowerCase().includes(kw))
      }
      return rows
    },
    currentWorkspaceIndex() {
      const id = String(this.currentQuestionId || '')
      return this.filteredWorkspaceRows.findIndex((x) => String(x.questionId) === id)
    },
    currentQuestion() {
      return this.rows.find((x) => String(x.questionId) === String(this.currentQuestionId)) || null
    },
    currentCodingJudgeDetail() {
      const q = this.currentQuestion
      if (!q || !this.isCodingType(q.type)) return null
      return parseJudgeDetail(q.judgeDetail)
    },
    currentCodingJudgeCaseResults() {
      const d = this.currentCodingJudgeDetail
      return d && Array.isArray(d.caseResults) ? d.caseResults : []
    },
    currentCodingBonusHits() {
      const d = this.currentCodingJudgeDetail
      return d && Array.isArray(d.structureHits) ? d.structureHits : []
    }
  },
  created() {
    this.readMetaFromHash()
    this.loadDetail()
  },
  unmounted() {
    this.clearMaterialBlobCache()
  },
  methods: {
    bonusHintLabel,
    truncateText(v) {
      const s = v == null ? '' : String(v)
      if (s.length <= 80) return s || '-'
      return s.slice(0, 77) + '...'
    },
    sanitizeRows(rows) {
      if (!Array.isArray(rows)) return []
      return rows.filter((row) => row && typeof row === 'object')
    },
    authHeaders(extra = {}) {
      let role = 'teacher'
      try {
        role = JSON.parse(localStorage.getItem('exam_user') || '{}').role || 'teacher'
      } catch (e) {
        role = 'teacher'
      }
      const h = { 'X-Role': role, ...extra }
      const rl = String(role).toLowerCase()
      if (rl === 'teacher') h['X-CSRF-Token'] = 'teacher-ui'
      if (rl === 'admin') h['X-CSRF-Token'] = 'exam-ui'
      return h
    },
    parseHashQuery() {
      const hash = window.location.hash || ''
      const idx = hash.indexOf('?')
      const raw = idx >= 0 ? hash.slice(idx + 1) : ''
      return new URLSearchParams(raw)
    },
    readMetaFromHash() {
      const q = this.parseHashQuery()
      this.recordMeta = {
        recordId: q.get('recordId') || null,
        userId: q.get('userId') || null,
        username: q.get('username') || '',
        examId: q.get('examId') || null,
        status: q.get('status') || '',
        score: Number(q.get('score') || 0),
        submitTime: q.get('submitTime') || ''
      }
    },
    goQuestions() {
      window.location.hash = '#/teacher/questions'
    },
    goPapers() {
      localStorage.setItem('teacher_active_menu', 'paper')
      window.location.hash = '#/teacher/papers'
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    goExamManage() {
      localStorage.setItem('teacher_active_menu', 'exam')
      window.location.hash = '#/teacher/papers'
    },
    backToGradingList() {
      localStorage.setItem('teacher_active_menu', 'grading')
      window.location.hash = '#/teacher/papers'
    },
    goExamRecordManage() {
      window.location.hash = '#/teacher/exam-records'
    },
    goProctor() {
      window.location.hash = '#/teacher/proctor'
    },
    goStudentUsers() {
      window.location.hash = '#/teacher/students'
    },
    isCodingType(type) {
      const t = String(type || '').toLowerCase()
      return t === 'coding' || t === 'python' || t.includes('coding') || t.includes('python')
    },
    isManualType(type) {
      const t = String(type || '').toLowerCase()
      return this.isCodingType(type) || t === MATERIAL_COLLECT_TYPE || t === SCRATCH_TYPE
    },
    isMaterialCollectType(type) {
      return String(type || '').toLowerCase() === MATERIAL_COLLECT_TYPE
    },
    isScratchType(type) {
      return String(type || '').toLowerCase() === SCRATCH_TYPE
    },
    scratchArtifact(row) {
      if (!row) return {}
      const art = row.scratchArtifact
      if (art && typeof art === 'object') return art
      return parseScratchAnswer(row.userAnswer)
    },
    openScratchGradingFullPage(row) {
      const cq = row || this.currentQuestion
      if (!cq || !this.isScratchType(cq.type)) return
      if (!this.recordMeta || !this.recordMeta.recordId) {
        window.alert('缺少考试记录，无法打开')
        return
      }
      const a = this.scratchArtifact(cq)
      if (!a || (!String(a.storageName || '').trim() && !String(a.downloadUrl || '').trim())) {
        window.alert('该生尚未上传 Scratch 项目')
        return
      }
      try {
        sessionStorage.setItem('scratch_grade_return_hash', window.location.hash || '#/teacher/grading')
        sessionStorage.setItem(
          'scratch_grade_context',
          JSON.stringify({
            recordId: this.recordMeta.recordId,
            questionId: cq.questionId,
            title: cq.title || '',
            analysis: cq.analysis || ''
          })
        )
      } catch (e) {
        void e
      }
      window.location.hash = '#/teacher/scratch-grade'
      if (typeof window !== 'undefined' && window.__examScratchCoreLoaded) {
        window.location.reload()
      }
    },
    materialCollectDraftText(row) {
      if (!row) return ''
      return parseMaterialAnswer(row.userAnswer).text
    },
    materialCollectAttList(row) {
      if (!row) return []
      if (Array.isArray(row.materialAttachments) && row.materialAttachments.length) {
        return row.materialAttachments
      }
      return parseMaterialAnswer(row.userAnswer).attachments
    },
    clearMaterialBlobCache() {
      const m = this.materialBlobUrlByKey || {}
      Object.keys(m).forEach((k) => {
        try {
          URL.revokeObjectURL(m[k])
        } catch (e) {
          void e
        }
      })
      this.materialBlobUrlByKey = {}
    },
    materialBlobCacheKey(att, kind) {
      const base = att && att.storageName ? String(att.storageName) : String((att && att.viewUrl) || (att && att.downloadUrl) || '')
      return `${base}|${kind}`
    },
    isMaterialImageAttachment(att) {
      if (!att || !att.viewUrl) return false
      const name = String(att.originalName || att.storageName || '')
      const ext = extOfFilename(name)
      return new Set(['.jpg', '.jpeg', '.png', '.gif']).has(ext)
    },
    materialImageDisplaySrc(att) {
      if (!this.isMaterialImageAttachment(att)) return ''
      const key = this.materialBlobCacheKey(att, 'view')
      return this.materialBlobUrlByKey[key] || ''
    },
    async ensureAuthBlobUrl(att, kind) {
      const path = kind === 'download' ? att && att.downloadUrl : att && att.viewUrl
      if (!path) return ''
      const key = this.materialBlobCacheKey(att, kind)
      if (this.materialBlobUrlByKey[key]) return this.materialBlobUrlByKey[key]
      const res = await fetch(String(path).trim(), { headers: this.authHeaders() })
      if (!res.ok) {
        window.alert(`附件加载失败（HTTP ${res.status}），请确认已登录教师账号后重试`)
        return ''
      }
      const blob = await res.blob()
      const obj = URL.createObjectURL(blob)
      this.materialBlobUrlByKey = { ...this.materialBlobUrlByKey, [key]: obj }
      return obj
    },
    async prefetchMaterialBlobsForRow(row) {
      if (!row || !this.isMaterialCollectType(row.type)) return
      const list = this.materialCollectAttList(row)
      for (let i = 0; i < list.length; i++) {
        const att = list[i]
        if (this.isMaterialImageAttachment(att)) {
          try {
            await this.ensureAuthBlobUrl(att, 'view')
          } catch (e) {
            void e
          }
        }
      }
    },
    openScratchDownload(row) {
      if (!row) return
      const a = this.scratchArtifact(row)
      if (!a.downloadUrl) {
        window.alert('暂无可下载的 Scratch 项目')
        return
      }
      const att = {
        storageName: a.storageName,
        originalName: a.originalName || 'project.sb3',
        downloadUrl: a.downloadUrl,
        viewUrl: a.viewUrl || a.downloadUrl
      }
      this.openMaterialWithAuth(att, 'download')
    },
    openMaterialWithAuth(att, mode) {
      const kind = mode === 'download' ? 'download' : 'view'
      this.ensureAuthBlobUrl(att, kind)
        .then((blobUrl) => {
          if (!blobUrl) return
          if (mode === 'download') {
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = String((att && att.originalName) || (att && att.storageName) || '附件')
            document.body.appendChild(a)
            a.click()
            a.remove()
            return
          }
          window.open(blobUrl, '_blank', 'noopener,noreferrer')
        })
        .catch(() => window.alert('打开附件失败，请检查网络'))
    },
    openMaterialImagePreview(att) {
      this.ensureAuthBlobUrl(att, 'view')
        .then((blobUrl) => {
          if (blobUrl) this.openGradingImg(blobUrl)
          else window.alert('图片尚未加载完成，请稍后再试')
        })
        .catch(() => window.alert('加载图片失败'))
    },
    formatMaterialFileSize(n) {
      const b = Number(n)
      if (!Number.isFinite(b) || b < 0) return ''
      if (b < 1024) return `${Math.round(b)} B`
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
      return `${(b / (1024 * 1024)).toFixed(2)} MB`
    },
    isAnswerManuallyGraded(row) {
      if (!row) return false
      if (row.manualGraded === true || row.manualGraded === 1) return true
      if (String(row.manualGraded) === '1') return true
      return false
    },
    pickQuestionById(questionId, options = {}) {
      const sameQuestion = String(questionId) === String(this.currentQuestionId)
      this.currentQuestionId = questionId
      const row = this.rows.find((x) => String(x.questionId) === String(questionId))
      if (!sameQuestion && !options.keepRunOutput) {
        this.runStdout = ''
        this.runStderr = ''
        this.runJudgeText = ''
      }
      if (row && this.isCodingType(row.type) && !this.isMaterialCollectType(row.type)) {
        this.runCode = row.userAnswer == null ? '' : String(row.userAnswer)
      } else {
        this.runCode = ''
      }
      if (row && this.isMaterialCollectType(row.type)) {
        this.prefetchMaterialBlobsForRow(row)
      }
    },
    gradingStemImages(q) {
      return parseUrlList(q && q.contentImages)
    },
    gradingAnalysisImages(q) {
      return parseUrlList(q && q.analysisImages)
    },
    gradingOptionImages(q) {
      if (!q) return []
      return buildQuestionOptionEntries(q)
        .filter((x) => x.imageUrl)
        .map((x) => ({ url: x.imageUrl, label: '选项 ' + x.key }))
    },
    openGradingImg(u) {
      const s = String(u || '').trim()
      if (!s) return
      this.gradingImgScale = 1
      this.gradingImgPreview = s
    },
    closeGradingImg() {
      this.gradingImgPreview = null
      this.gradingImgScale = 1
    },
    onGradingImgWheel(e) {
      if (!this.gradingImgPreview) return
      const dy = e.deltaY || 0
      const step = dy > 0 ? -0.12 : 0.12
      this.gradingImgScale = Math.min(4, Math.max(0.25, this.gradingImgScale + step))
    },
    goPrevQuestion() {
      if (!this.filteredWorkspaceRows.length) return
      const idx = this.currentWorkspaceIndex
      if (idx <= 0) {
        this.pickQuestionById(this.filteredWorkspaceRows[0].questionId)
        return
      }
      this.pickQuestionById(this.filteredWorkspaceRows[idx - 1].questionId)
    },
    goNextPendingQuestion() {
      if (!this.filteredWorkspaceRows.length) return
      const pending = this.pendingManualRows
      if (pending.length > 0) {
        const currentPendingIdx = pending.findIndex((x) => String(x.questionId) === String(this.currentQuestionId))
        if (currentPendingIdx >= 0 && currentPendingIdx < pending.length - 1) {
          this.pickQuestionById(pending[currentPendingIdx + 1].questionId)
          return
        }
        this.pickQuestionById(pending[0].questionId)
        return
      }
      const idx = this.currentWorkspaceIndex
      if (idx < 0 || idx >= this.filteredWorkspaceRows.length - 1) {
        this.pickQuestionById(this.filteredWorkspaceRows[0].questionId)
        return
      }
      this.pickQuestionById(this.filteredWorkspaceRows[idx + 1].questionId)
    },
    loadDetail() {
      if (!this.recordMeta.recordId) {
        window.alert('缺少 recordId，无法进入阅卷工作台')
        return
      }
      const previousQuestionId = this.currentQuestionId
      fetch(`/api/student/exam/answers/record/${this.recordMeta.recordId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            this.clearMaterialBlobCache()
            this.rows = this.sanitizeRows(res.data)
            const draft = {}
            this.rows.forEach((row) => {
              if (row && this.isManualType(row.type)) {
                draft[row.questionId] = Number(row.answerScore ?? 0)
              }
            })
            this.manualScoreDraft = draft
            const scDraft = {}
            this.rows.forEach((row) => {
              if (row && this.isScratchType(row.type)) {
                const a = this.scratchArtifact(row)
                scDraft[row.questionId] = String(a.teacherComment || '')
              }
            })
            this.scratchCommentDraft = scDraft
            const kept = previousQuestionId != null
              ? this.rows.find((x) => String(x.questionId) === String(previousQuestionId))
              : null
            if (kept) {
              this.pickQuestionById(previousQuestionId, { keepRunOutput: true })
              return
            }
            const first = this.pendingManualRows[0] || this.filteredWorkspaceRows[0] || this.workspaceRows[0] || null
            if (first) this.pickQuestionById(first.questionId)
            return
          }
          window.alert(res.message || '加载阅卷详情失败')
        })
    },
    fillCodeFromAnswer() {
      if (!this.currentQuestion) return
      this.runCode = this.currentQuestion.userAnswer || ''
    },
    runPythonCode() {
      if (!this.currentQuestion || !this.isCodingType(this.currentQuestion.type)) {
        window.alert('当前题目不是 Python 编程题')
        return
      }
      if (!this.runCode.trim()) {
        window.alert('请先输入 Python 代码')
        return
      }
      this.runningCode = true
      this.runJudgeText = '运行中...'
      this.runCaseResults = []
      this.runStdout = '环境准备中（Python 运行环境准备中…）'
      this.runStderr = ''
      fetch('/api/teacher/question/questions/coding/preview-judge', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          questionId: this.currentQuestion.questionId,
          code: this.runCode,
          recordId: this.recordMeta.recordId
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            const data = res.data || {}
            this.runStdout = data.stdout || ''
            this.runStderr = data.stderr || ''
            this.runCaseResults = Array.isArray(data.caseResults) ? data.caseResults : []
            const passed = data.passedCases != null ? data.passedCases : 0
            const total = data.totalCases != null ? data.totalCases : 0
            this.runJudgeText = data.isCorrect
              ? `通过（${passed}/${total}），参考得分 ${data.score || 0}（预览，未写入成绩）`
              : `未通过（${passed}/${total}），参考得分 ${data.score || 0}（预览，未写入成绩）`
            return
          }
          const msg = String(res && res.message ? res.message : '代码运行失败')
          const isPythonEnvPreparing = msg.includes('Python') && msg.includes('稍后重试')
          this.runJudgeText = isPythonEnvPreparing ? '环境准备中，请稍后重试' : '运行失败'
          this.runStderr = ''
          window.alert(isPythonEnvPreparing ? '环境准备中，请稍后重试' : msg)
        })
        .catch((e) => {
          this.runStderr = e && e.message ? e.message : '请求失败'
          this.runJudgeText = '运行失败'
          window.alert('运行代码失败，请检查后端 Python 环境或接口状态')
        })
        .finally(() => {
          this.runningCode = false
        })
    },
    saveSingleScore(row) {
      if (!row || !this.isManualType(row.type)) return
      const maxScore = Number(row.maxScore || 0)
      let score = Number(this.manualScoreDraft[row.questionId] ?? 0)
      if (!Number.isFinite(score) || score < 0) score = 0
      if (score > maxScore) score = maxScore
      const api = this.isCodingType(row.type) ? 'grade/coding' : 'grade/manual'
      const params = new URLSearchParams({
        recordId: String(this.recordMeta.recordId),
        questionId: String(row.questionId),
        score: String(score)
      })
      if (this.isScratchType(row.type)) {
        const c = String(this.scratchCommentDraft[row.questionId] || '').trim()
        if (c) params.append('comment', c)
      }
      fetch(`/api/student/exam/${api}?${params.toString()}`, {
        method: 'POST',
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            this.loadDetailAfterScoreSave()
            return
          }
          window.alert(res.message || '保存评分失败')
        })
        .catch(() => window.alert('保存评分失败，请检查网络或后端是否已更新数据库字段 manual_graded'))
    },
    saveSingleScoreAndNext(row) {
      if (!row || !this.isManualType(row.type)) {
        this.goNextPendingQuestion()
        return
      }
      const maxScore = Number(row.maxScore || 0)
      let score = Number(this.manualScoreDraft[row.questionId] ?? 0)
      if (!Number.isFinite(score) || score < 0) score = 0
      if (score > maxScore) score = maxScore
      const api = this.isCodingType(row.type) ? 'grade/coding' : 'grade/manual'
      const params = new URLSearchParams({
        recordId: String(this.recordMeta.recordId),
        questionId: String(row.questionId),
        score: String(score)
      })
      if (this.isScratchType(row.type)) {
        const c = String(this.scratchCommentDraft[row.questionId] || '').trim()
        if (c) params.append('comment', c)
      }
      fetch(`/api/student/exam/${api}?${params.toString()}`, {
        method: 'POST',
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            this.loadDetailAfterScoreSave()
            return
          }
          window.alert(res.message || '保存评分失败')
        })
        .catch(() => window.alert('保存评分失败，请检查网络或后端是否已更新数据库字段 manual_graded'))
    },
    loadDetailAfterScoreSave() {
      fetch(`/api/student/exam/answers/record/${this.recordMeta.recordId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '刷新阅卷详情失败')
            return
          }
          this.clearMaterialBlobCache()
          this.rows = this.sanitizeRows(res.data)
          const draft = {}
          this.rows.forEach((row) => {
            if (row && this.isManualType(row.type)) {
              draft[row.questionId] = Number(row.answerScore ?? 0)
            }
          })
          this.manualScoreDraft = draft
          const scDraft = {}
          this.rows.forEach((r) => {
            if (r && this.isScratchType(r.type)) {
              const a = this.scratchArtifact(r)
              scDraft[r.questionId] = String(a.teacherComment || '')
            }
          })
          this.scratchCommentDraft = scDraft

          const pending = this.pendingManualRows
          if (pending.length === 0) {
            this.finalizeAndSwitchToNextStudent()
            return
          }

          const currentPendingIdx = pending.findIndex((x) => String(x.questionId) === String(this.currentQuestionId))
          if (currentPendingIdx >= 0 && currentPendingIdx < pending.length - 1) {
            this.pickQuestionById(pending[currentPendingIdx + 1].questionId)
            return
          }
          this.pickQuestionById(pending[0].questionId)
        })
    },
    finalizeAndSwitchToNextStudent() {
      const params = new URLSearchParams({ recordId: String(this.recordMeta.recordId) })
      fetch(`/api/student/exam/grade/submit?${params.toString()}`, {
        method: 'POST',
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            this.recordMeta.status = 'GRADED'
            this.recordMeta.score = Number((res.data && res.data.totalScore) || this.recordMeta.score || 0)
            this.trySwitchToNextUngradedStudent()
            return
          }
          window.alert(res.message || '提交阅卷失败，暂无法自动切换学员')
        })
        .catch(() => window.alert('提交阅卷失败，暂无法自动切换学员'))
    },
    buildGradingHash(row) {
      const params = new URLSearchParams({
        recordId: String(row.recordId || ''),
        userId: String(row.userId || ''),
        username: String(row.username || ''),
        status: String(row.status || ''),
        score: String(row.score || 0),
        submitTime: String(row.submitTime || ''),
        examId: String(this.recordMeta.examId || '')
      })
      return `#/teacher/grading?${params.toString()}`
    },
    trySwitchToNextUngradedStudent() {
      const examId = this.recordMeta.examId
      if (!examId) {
        window.alert('该学生已经完成全部阅卷')
        return
      }
      fetch(`/api/student/exam/answers/exam/${examId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert('该学生已经完成全部阅卷')
            return
          }
          const rows = res.data || []
          const next = rows.find((x) => {
            const status = String(x.status || '').toUpperCase()
            const submitted = String(x.submitTime || '').trim().length > 0
            return String(x.recordId) !== String(this.recordMeta.recordId) && submitted && status !== 'GRADED'
          })
          if (!next) {
            window.alert('该学生已经完成全部阅卷，且没有下一个未评分学员')
            return
          }
          window.alert(`该学生已经完成全部阅卷，已切换到下一个学员：${next.username || next.userId}`)
          window.location.hash = this.buildGradingHash(next)
          this.readMetaFromHash()
          this.rows = []
          this.currentQuestionId = null
          this.manualScoreDraft = {}
          this.scratchCommentDraft = {}
          this.loadDetail()
        })
        .catch(() => {
          window.alert('该学生已经完成全部阅卷')
        })
    },
    submitRecordGrade() {
      if (!this.recordMeta.recordId) return
      const params = new URLSearchParams({ recordId: String(this.recordMeta.recordId) })
      fetch(`/api/student/exam/grade/submit?${params.toString()}`, {
        method: 'POST',
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            this.recordMeta.status = 'GRADED'
            this.recordMeta.score = Number((res.data && res.data.totalScore) || this.recordMeta.score || 0)
            window.alert('已提交阅卷')
            return
          }
          window.alert(res.message || '提交阅卷失败')
        })
    }
  }
}
</script>

<style scoped>
.teacher-page { min-height: 100vh; padding: 20px; background: linear-gradient(160deg, #f7faff, #eef4ff); color: #1f2a44; }
.side-nav { width: 240px; min-height: calc(100vh - 40px); margin-right: 16px; padding: 16px; display: flex; flex-direction: column; gap: 10px; background: rgba(255, 255, 255, 0.78); border: 1px solid #d8e6ff; border-radius: 12px; box-shadow: 0 12px 30px rgba(63, 108, 199, 0.1); }
.side-nav { flex-shrink: 0; }
.side-nav.collapsed { width: 76px; }
.side-nav.collapsed .nav-text { display: none; }
.side-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.collapse-btn { width: 28px; height: 28px; border-radius: 8px; border: 1px solid #d8e6ff; background: #f6f9ff; color: #335896; font-size: 12px; }
.sub-btn { border: 1px solid #d8e6ff; border-radius: 10px; padding: 10px 12px; color: #335896; background: #f6f9ff; text-align: left; transition: all 0.2s ease; }
.sub-btn:hover { background: #eef5ff; border-color: #bfd6ff; transform: translateY(-1px); }
.sub-btn.active { background: linear-gradient(120deg, #2f6feb, #5ca2ff); color: #fff; border-color: transparent; }
.content-area { flex: 1; }
.topbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; padding: 14px 16px; border-radius: 12px; background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92)); border: 1px solid #d8e6ff; box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1); }
.actions { display: flex; gap: 8px; }
.card-box { background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92)); border: 1px solid #d8e6ff; border-radius: 12px; padding: 16px; box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1); }
.meta-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.k { color: #5c7098; margin-right: 6px; }
.v { color: #1f2a44; font-weight: 600; }
.workspace { display: grid; grid-template-columns: 1.2fr 1fr; gap: 14px; }
.left-pane, .right-pane { min-width: 0; }
.question-list { max-height: 280px; overflow: auto; display: flex; flex-direction: column; gap: 8px; }
.q-btn { text-align: left; border: 1px solid #d8e6ff; border-radius: 8px; background: #f6f9ff; color: #2f4f8f; padding: 8px 10px; }
.q-btn.active { background: #e9f2ff; border-color: #9bc0ff; color: #173a78; }
.code-editor { min-height: 220px; font-family: Consolas, "Courier New", monospace; }
.run-box { margin: 0; background: #0f172a; color: #d7e2ff; border-radius: 10px; padding: 10px; min-height: 80px; white-space: pre-wrap; word-break: break-word; }
.result-shell { border: 1px solid rgba(145, 180, 240, 0.35); border-radius: 12px; padding: 8px; background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92)); }
.modal-wide { width: min(1200px, 100%); }
@media (max-width: 992px) { .teacher-page { flex-direction: column; } .side-nav { width: 100%; min-height: auto; margin-right: 0; margin-bottom: 12px; } .workspace { grid-template-columns: 1fr; } .meta-grid { grid-template-columns: 1fr; } }
.gw-q-thumb { max-width: 120px; max-height: 72px; object-fit: contain; border-radius: 6px; border: 1px solid #c5d6f5; cursor: zoom-in; background: #fff; }
.gw-img-mask { position: fixed; inset: 0; z-index: 3000; background: rgba(15, 23, 42, 0.55); display: flex; align-items: center; justify-content: center; padding: 16px; }
.gw-img-panel { background: #fff; border-radius: 12px; padding: 12px 14px 14px; max-width: min(96vw, 960px); box-shadow: 0 20px 50px rgba(15, 23, 42, 0.35); }
.gw-img-viewport { overflow: auto; max-height: min(78vh, 760px); display: flex; align-items: center; justify-content: center; padding: 8px; background: #f1f5f9; border-radius: 8px; }
.gw-img-zoomed { max-width: min(90vw, 900px); height: auto; transform-origin: center center; transition: transform 0.08s ease-out; }
.gw-pre { white-space: pre-wrap; word-break: break-word; margin: 0; padding: 10px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; max-height: 220px; overflow: auto; }
.gw-stem-body { max-height: min(40vh, 360px); font-family: Consolas, "Courier New", ui-monospace, monospace; font-size: 13px; line-height: 1.45; }
.gw-material-block .gw-mat-thumb { width: 56px; height: 56px; object-fit: cover; border-radius: 6px; border: 1px solid #c5d6f5; }
.gw-mat-thumb-wrap { line-height: 0; }
.gw-mat-thumb-ph { display: inline-flex; align-items: center; justify-content: center; background: #f1f5f9; }
.gw-mat-side-list { max-height: min(60vh, 520px); overflow: auto; }
.gw-mat-side-thumb { padding: 0; overflow: hidden; border-radius: 8px; max-width: 100%; }
.gw-mat-side-thumb img { display: block; max-width: 100%; max-height: 160px; object-fit: contain; }
</style>
